"use client"

import * as React from "react"

import { Stagger } from "@/components/motion/reveal"
import { PricingCard, type PricingCardPlan } from "@/components/sections/pricing-card"
import { cn } from "@/lib/utils"
import { BILLING_CYCLE_LABEL, toFaDigits, type BillingCycle } from "@/lib/format"

/**
 * Pricing grid with a billing-cycle switch.
 *
 * The switch is client state rather than a URL param: it's a comparison aid,
 * not a destination, and keeping it local avoids a round-trip on every toggle.
 * Only the selected period button is green — prices and cards keep their
 * normal colors.
 */
export function PricingGrid({
  plans,
  cycles = ["monthly", "annually"],
  defaultCycle = "monthly",
  columns = 4,
  showCycleSwitch = true,
  className,
}: {
  plans: PricingCardPlan[]
  cycles?: BillingCycle[]
  defaultCycle?: BillingCycle
  columns?: 3 | 4
  showCycleSwitch?: boolean
  className?: string
}) {
  const available = cycles.length ? cycles : (["monthly"] as BillingCycle[])
  const [cycle, setCycle] = React.useState<BillingCycle>(
    available.includes(defaultCycle) ? defaultCycle : available[0],
  )

  const bestDiscount = React.useMemo(() => {
    if (cycle === "quarterly") {
      return Math.max(0, ...plans.map((plan) => plan.quarterlyDiscount ?? 0))
    }
    if (cycle === "annually") {
      return Math.max(0, ...plans.map((plan) => plan.annualDiscount ?? 0))
    }
    return 0
  }, [cycle, plans])
  const discountLabel = toFaDigits(
    cycle === "annually" ? 3.5 : Math.round(bestDiscount * 100),
  )

  function choose(option: BillingCycle) {
    if (option === cycle) return
    setCycle(option)
  }

  function onRadioKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const current = available.indexOf(cycle)
    if (current < 0) return
    let next = current
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = current + 1
    if (event.key === "ArrowRight" || event.key === "ArrowDown") next = current - 1
    if (event.key === "Home") next = 0
    if (event.key === "End") next = available.length - 1
    if (next === current) return
    event.preventDefault()
    const wrapped = (next + available.length) % available.length
    choose(available[wrapped])
  }

  return (
    <div className={cn(className)}>
      {showCycleSwitch && available.length > 1 ? (
        <div className="mb-8 flex flex-col items-center gap-2.5">
          <div
            role="radiogroup"
            aria-label="دوره پرداخت"
            data-cycle={cycle}
            onKeyDown={onRadioKeyDown}
            className={cn(
              "inline-flex max-w-full flex-wrap items-center justify-center gap-1 rounded-[var(--radius-lg)] p-1",
              "border border-[var(--hairline)] bg-[var(--surface)] shadow-xs",
            )}
          >
            {available.map((option) => {
              const active = option === cycle
              const saveOption = option !== "monthly"
              return (
                <button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  tabIndex={active ? 0 : -1}
                  onClick={() => choose(option)}
                  className={cn(
                    "inline-flex min-h-10 items-center gap-1.5 rounded-[var(--radius-sm)] px-3 py-2 sm:px-4",
                    "text-[0.8125rem] font-semibold sm:text-sm",
                    "transition-[background-color,color,box-shadow] duration-200 ease-[var(--ease-out-quint)]",
                    active &&
                      "bg-success-500 text-white shadow-[0_8px_16px_-10px_rgb(5_150_105/0.85)]",
                    !active && "text-[var(--content-muted)] hover:bg-[var(--canvas-subtle)] hover:text-[var(--content)]",
                  )}
                >
                  {BILLING_CYCLE_LABEL[option]}
                  {saveOption && bestDiscount > 0 ? (
                    <span
                      className={cn(
                        "rounded-full px-1.5 py-0.5 text-[0.625rem] font-extrabold leading-none",
                        active
                          ? "bg-white/20 text-white"
                          : "bg-success-500/12 text-success-700 dark:text-success-300",
                      )}
                    >
                      {discountLabel}٪
                    </span>
                  ) : null}
                </button>
              )
            })}
          </div>
          <p
            aria-live="polite"
            className="min-h-7 px-3 text-center text-[0.8125rem] font-semibold text-[var(--content-subtle)]"
          >
            {bestDiscount > 0
              ? cycle === "quarterly"
                ? `پلن سه‌ماهه ${discountLabel}٪ تخفیف دارد`
                : `پلن سالانه ${discountLabel}٪ تخفیف دارد`
              : "\u00a0"}
          </p>
        </div>
      ) : null}

      <Stagger
        as="ul"
        step={0.07}
        className={cn(
          "grid items-stretch gap-6",
          "sm:grid-cols-2",
          columns === 4 ? "xl:grid-cols-4" : "lg:grid-cols-3",
        )}
      >
        {plans.map((plan) => (
          <PricingCard
            key={plan.id}
            plan={plan}
            cycle={cycle}
          />
        ))}
      </Stagger>
    </div>
  )
}
